import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Save, Check, Plus, Trash2, ArrowLeft, ExternalLink, Download, Eye, EyeOff, ArrowUp, ArrowDown, Pencil } from 'lucide-react';
import { motion } from 'framer-motion';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';
import { supportsCapability } from '../lib/capabilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TEMPLATES = [
  { id: 'modern',  name: 'Modern',  desc: 'Dark, cinematic — matches the host dashboard aesthetic' },
  { id: 'classic', name: 'Classic', desc: 'Light, serif typography for traditional events' },
  { id: 'minimal', name: 'Minimal', desc: 'Geometric, neutral palette' },
];

export default function AuraSiteBuilderPage() {
  const { auraId } = useParams();
  const [site, setSite] = useState({ template: 'modern', is_published: false, content: { welcome: '', location: '', dress_code: '', schedule: [] } });
  const [auraKey, setAuraKey] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [rsvps, setRsvps] = useState(null);
  const [editingRsvp, setEditingRsvp] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsSaving, setQuestionsSaving] = useState(false);
  const [questionsSaved, setQuestionsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/auras/${auraId}`, { withCredentials: true }).then(r => { setAuraKey(r.data.aura_key); setCapabilities(r.data.capabilities); }),
      axios.get(`${API_URL}/api/auras/${auraId}/site`, { withCredentials: true }).then(({ data }) => {
        setSite({
          template: data.template || 'modern',
          is_published: !!data.is_published,
          content: {
            welcome: data.content?.welcome || '',
            location: data.content?.location || '',
            dress_code: data.content?.dress_code || '',
            schedule: Array.isArray(data.content?.schedule) ? data.content.schedule : [],
          }
        });
      }),
      axios.get(`${API_URL}/api/auras/${auraId}/rsvp-questions`, { withCredentials: true })
        .then(r => setQuestions(Array.isArray(r.data) ? r.data : []))
        .catch(() => setQuestions([])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [auraId]);

  const QUESTION_TYPES = [
    { id: 'text', name: 'Short text' },
    { id: 'textarea', name: 'Long text' },
    { id: 'select', name: 'Single choice' },
    { id: 'multiselect', name: 'Multi-choice' },
    { id: 'checkbox', name: 'Yes/No' },
    { id: 'meal', name: 'Meal preference' },
    { id: 'plus_ones', name: 'Plus ones' },
  ];

  const addQuestion = () => setQuestions(q => [...q, {
    label: '', type: 'text', options: null, required: false, order: q.length, plus_ones_cap: null,
  }]);
  const removeQuestion = (i) => setQuestions(q => q.filter((_, idx) => idx !== i));
  const updateQuestion = (i, patch) => setQuestions(q => q.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const moveQuestion = (i, dir) => setQuestions(q => {
    const j = i + dir;
    if (j < 0 || j >= q.length) return q;
    const next = [...q];
    [next[i], next[j]] = [next[j], next[i]];
    return next.map((it, idx) => ({ ...it, order: idx }));
  });

  const saveQuestions = async () => {
    setQuestionsSaving(true);
    try {
      const payload = questions.map((q, i) => ({
        label: q.label || '', type: q.type,
        options: q.options && q.options.length ? q.options : null,
        required: !!q.required, order: i,
        plus_ones_cap: q.type === 'plus_ones' ? (q.plus_ones_cap || 5) : null,
      }));
      const { data } = await axios.put(`${API_URL}/api/auras/${auraId}/rsvp-questions`, payload, { withCredentials: true });
      setQuestions(Array.isArray(data) ? data : []);
      setQuestionsSaved(true);
      setTimeout(() => setQuestionsSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setQuestionsSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auras/${auraId}/site`, site, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    const next = !site.is_published;
    setSite(prev => ({ ...prev, is_published: next }));
    await axios.put(`${API_URL}/api/auras/${auraId}/site`, { ...site, is_published: next }, { withCredentials: true });
  };

  const fetchRsvps = async () => {
    const { data } = await axios.get(`${API_URL}/api/auras/${auraId}/rsvps`, { withCredentials: true });
    setRsvps(data);
  };

  const downloadRsvpCsv = async () => {
    const response = await axios.get(`${API_URL}/api/auras/${auraId}/rsvps?format=csv`, { withCredentials: true, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvps_${auraId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteRsvp = async (id) => {
    if (!window.confirm('Delete this RSVP?')) return;
    try {
      await axios.delete(`${API_URL}/api/auras/${auraId}/rsvps/${id}`, { withCredentials: true });
      await fetchRsvps();
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const saveRsvpEdit = async () => {
    try {
      const { id, guest_name, email, attending, party_size, dietary_notes, message } = editingRsvp;
      await axios.put(`${API_URL}/api/auras/${auraId}/rsvps/${id}`,
        { guest_name, email, attending, party_size: parseInt(party_size, 10) || 1, dietary_notes, message },
        { withCredentials: true });
      setEditingRsvp(null);
      await fetchRsvps();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const updateContent = (key, val) => setSite(prev => ({ ...prev, content: { ...prev.content, [key]: val } }));

  const addScheduleRow = () => updateContent('schedule', [...site.content.schedule, { time: '', event: '' }]);
  const updateScheduleRow = (i, key, val) => {
    const next = [...site.content.schedule];
    next[i] = { ...next[i], [key]: val };
    updateContent('schedule', next);
  };
  const removeScheduleRow = (i) => updateContent('schedule', site.content.schedule.filter((_, idx) => idx !== i));

  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center"><Sparkles className="w-8 h-8 text-champagne animate-pulse" /></div>;

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 py-3 sticky top-0 bg-midnight z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2 flex-wrap">
          <Link to={`/aura/${auraId}`} className="text-text-muted hover:text-champagne flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to aura
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={togglePublish} className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${site.is_published ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'btn-outline'}`}>
              {site.is_published ? <><Eye className="w-3 h-3" /> Live</> : <><EyeOff className="w-3 h-3" /> Draft</>}
            </button>
            {site.is_published && auraKey && (
              <a href={`/event/${auraKey}`} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> View site
              </a>
            )}
            <button onClick={handleSave} disabled={saving} className="btn-champagne text-sm px-4 py-2 flex items-center gap-2">
              {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saved ? 'Saved' : saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Template */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="aura-card rounded-2xl p-6">
          <h2 className="text-lg font-serif font-medium text-text-light mb-4">Template</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {TEMPLATES.map(t => (
              <button
                key={t.id}
                onClick={() => setSite(prev => ({ ...prev, template: t.id }))}
                className={`p-4 rounded-xl border transition-all text-left ${site.template === t.id ? 'border-champagne bg-champagne/10' : 'border-champagne/20 hover:border-champagne/40'}`}
              >
                <p className="font-medium text-text-light mb-1">{t.name}</p>
                <p className="text-xs text-text-muted">{t.desc}</p>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="aura-card rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-serif font-medium text-text-light">Content</h2>
          <div>
            <label className="text-sm text-text-muted block mb-2">Welcome message</label>
            <textarea value={site.content.welcome} onChange={e => updateContent('welcome', e.target.value)} rows={4} className="aura-input w-full resize-none" placeholder="Welcome our friends and family to celebrate…" />
          </div>
          <div>
            <label className="text-sm text-text-muted block mb-2">Location</label>
            <input type="text" value={site.content.location} onChange={e => updateContent('location', e.target.value)} className="aura-input w-full" placeholder="The Grand Ballroom, 123 Main St, Lisbon" />
          </div>
          <div>
            <label className="text-sm text-text-muted block mb-2">Dress code</label>
            <input type="text" value={site.content.dress_code} onChange={e => updateContent('dress_code', e.target.value)} className="aura-input w-full" placeholder="Black-tie optional" />
          </div>
        </motion.div>

        {/* Schedule */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="aura-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-serif font-medium text-text-light">Schedule</h2>
            <button onClick={addScheduleRow} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          {site.content.schedule.length === 0 ? (
            <p className="text-text-muted text-sm">No items yet. Add a row to start building the schedule.</p>
          ) : (
            <div className="space-y-2">
              {site.content.schedule.map((row, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" placeholder="6:00 PM" value={row.time} onChange={e => updateScheduleRow(i, 'time', e.target.value)} className="aura-input w-32 text-sm" />
                  <input type="text" placeholder="Cocktail hour" value={row.event} onChange={e => updateScheduleRow(i, 'event', e.target.value)} className="aura-input flex-1 text-sm" />
                  <button onClick={() => removeScheduleRow(i)} className="p-2.5 text-text-muted hover:text-red-400 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Remove row">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* RSVP — questions editor + responses; only for RSVP-capable occasions */}
        {supportsCapability({ capabilities }, 'rsvp') && (<>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="aura-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-serif font-medium text-text-light">RSVP Questions</h2>
            <div className="flex gap-2">
              <button onClick={addQuestion} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add
              </button>
              <button onClick={saveQuestions} disabled={questionsSaving} className="btn-champagne text-xs px-3 py-1.5 flex items-center gap-1">
                {questionsSaved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
                {questionsSaved ? 'Saved' : questionsSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          {questions.length === 0 ? (
            <p className="text-text-muted text-sm">No custom questions. Add meal, plus-ones, or any other.</p>
          ) : (
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div key={i} className="bg-midnight/40 rounded-lg p-3 space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={q.label || ''} onChange={e => updateQuestion(i, { label: e.target.value })}
                      placeholder="Question label" className="aura-input flex-1 text-sm" />
                    <select value={q.type} onChange={e => updateQuestion(i, { type: e.target.value })}
                      className="aura-input text-sm py-2.5">
                      {QUESTION_TYPES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button onClick={() => moveQuestion(i, -1)} className="p-2.5 text-text-muted hover:text-champagne min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => moveQuestion(i, 1)} className="p-2.5 text-text-muted hover:text-champagne min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => removeQuestion(i)} className="p-2.5 text-text-muted hover:text-red-400 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Remove question"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex gap-3 items-center text-xs text-text-muted">
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={!!q.required} onChange={e => updateQuestion(i, { required: e.target.checked })} />
                      Required
                    </label>
                    {q.type === 'plus_ones' && (
                      <label className="flex items-center gap-1">
                        Cap:
                        <input type="number" min={1} max={20} value={q.plus_ones_cap || 5}
                          onChange={e => updateQuestion(i, { plus_ones_cap: parseInt(e.target.value || '1', 10) })}
                          className="aura-input w-16 text-xs" />
                      </label>
                    )}
                    {(q.type === 'select' || q.type === 'multiselect' || q.type === 'meal') && (
                      <input type="text" value={(q.options || []).join(', ')}
                        onChange={e => updateQuestion(i, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder={q.type === 'meal' ? 'Leave empty for defaults' : 'Option A, Option B, ...'}
                        className="aura-input flex-1 text-xs" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* RSVPs */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="aura-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-serif font-medium text-text-light">RSVPs</h2>
            <div className="flex gap-2">
              <button onClick={fetchRsvps} className="btn-outline text-xs px-3 py-1.5">Refresh</button>
              <button onClick={downloadRsvpCsv} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1">
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
          </div>
          {!rsvps ? (
            <p className="text-text-muted text-sm">Click Refresh to load.</p>
          ) : rsvps.summary.total === 0 ? (
            <p className="text-text-muted text-sm">No RSVPs yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4 text-center">
                <div><p className="text-2xl text-champagne">{rsvps.summary.yes}</p><p className="text-xs text-text-muted">Yes</p></div>
                <div><p className="text-2xl text-text-muted">{rsvps.summary.maybe}</p><p className="text-xs text-text-muted">Maybe</p></div>
                <div><p className="text-2xl text-text-muted">{rsvps.summary.no}</p><p className="text-xs text-text-muted">No</p></div>
                <div><p className="text-2xl text-champagne">{rsvps.summary.total_attending_guests}</p><p className="text-xs text-text-muted">Total guests</p></div>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {rsvps.rsvps.map(r => (
                  <div key={r.id} className="bg-midnight/50 rounded-lg p-3 text-sm">
                    {editingRsvp?.id === r.id ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input className="aura-input flex-1 text-sm" value={editingRsvp.guest_name || ''}
                            onChange={e => setEditingRsvp({ ...editingRsvp, guest_name: e.target.value })} placeholder="Name" />
                          <input type="number" min={1} max={20} className="aura-input w-20 text-sm" value={editingRsvp.party_size || 1}
                            onChange={e => setEditingRsvp({ ...editingRsvp, party_size: e.target.value })} />
                          <select className="aura-input text-sm py-2" value={editingRsvp.attending || 'yes'}
                            onChange={e => setEditingRsvp({ ...editingRsvp, attending: e.target.value })}>
                            <option value="yes">yes</option><option value="maybe">maybe</option><option value="no">no</option>
                          </select>
                        </div>
                        <input className="aura-input w-full text-sm" value={editingRsvp.email || ''}
                          onChange={e => setEditingRsvp({ ...editingRsvp, email: e.target.value })} placeholder="Email" />
                        <input className="aura-input w-full text-sm" value={editingRsvp.dietary_notes || ''}
                          onChange={e => setEditingRsvp({ ...editingRsvp, dietary_notes: e.target.value })} placeholder="Dietary notes" />
                        <input className="aura-input w-full text-sm" value={editingRsvp.message || ''}
                          onChange={e => setEditingRsvp({ ...editingRsvp, message: e.target.value })} placeholder="Message" />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditingRsvp(null)} className="btn-outline text-xs px-3 py-1.5">Cancel</button>
                          <button onClick={saveRsvpEdit} className="btn-champagne text-xs px-3 py-1.5 flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0">
                            <p className="text-text-light">{r.guest_name} <span className="text-text-muted">×{r.party_size}</span></p>
                            {r.email && <p className="text-text-muted text-xs">{r.email}</p>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded ${r.attending === 'yes' ? 'bg-green-500/20 text-green-400' : r.attending === 'no' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {r.attending}
                            </span>
                            <button onClick={() => setEditingRsvp(r)} aria-label="Edit RSVP" className="p-1.5 text-text-muted hover:text-champagne"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteRsvp(r.id)} aria-label="Delete RSVP" className="p-1.5 text-text-muted hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        {r.dietary_notes && <p className="text-text-muted text-xs mt-1">Dietary: {r.dietary_notes}</p>}
                        {r.message && <p className="text-text-muted text-xs italic mt-1">"{r.message}"</p>}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
        </>)}
      </div>
      <FooterExpanded />
    </div>
  );
}
